import {
  SNAPSHOT_BUFFER_LIMIT,
  SNAPSHOT_BUFFER_RETENTION_MS,
  SNAPSHOT_RENDER_DELAY_MIN_MS,
  SNAPSHOT_RENDER_DELAY_MAX_MS,
  SNAPSHOT_INTERP_BASE_MARGIN_MS,
  SNAPSHOT_INTERP_JITTER_MULT,
  SNAPSHOT_RENDER_DELAY_SLEW_MS_PER_SEC,
  REMOTE_DISPLAY_SMOOTH_TAU_MS,
  REMOTE_DISPLAY_FOLD_MIN_PX,
  REMOTE_DISPLAY_FOLD_SPEED_MULT,
  REMOTE_DISPLAY_FOLD_MIN_RAD,
  REMOTE_DISPLAY_MAX_OFFSET_PX,
  REMOTE_DISPLAY_MAX_OFFSET_RAD,
  SNAPSHOT_EXTRAP_MAX_MS,
  SNAPSHOT_EXTRAP_LEAD,
  SNAPSHOT_EXTRAP_HARD_MAX_MS,
  MOTION_MIN_SPEED_PX_PER_MS,
  MOTION_MAX_COMMIT_GAP_MS,
  MOTION_MAX_OMEGA_RAD_PER_MS,
  LOCAL_CORRECTION_SMOOTH_MS,
  LOCAL_CORRECTION_MAX_PX,
  LOCAL_CORRECTION_MAX_RAD,
  CONTROL_RESPONSIVE_WINDOW_MS,
  LOCAL_HEAD_TRAIL_ATTACH_MAX_PX,
  LOCAL_HEAD_ACTIVE_ATTACH_MAX_PX,
  ACTIVE_PREDICTION_LEAD_MAX_MS,
  IDLE_PREDICTION_LEAD_MAX_MS,
  IDLE_PREDICTION_LEAD_MARGIN_MS,
  ENABLE_LOCAL_PREDICTION,
  USE_AUTHORITATIVE_SNAPSHOTS_ONLY,
  LOCAL_INPUT_APPLY_EPSILON_MS
} from '../engine/config.js';
import { model, authoritative, keyState, net } from '../engine/state.js';
import {
  clamp,
  lerp,
  angleDiff,
  lerpAngle,
  cloneSnapshotStatus,
  cloneEffectState,
  toSnapshotStruct,
  cloneFrameState
} from '../utils/utils.js';
import { advancePredictedPose } from '../../core/motion-core.js';
import { estimatedOneWayMs, renderNowMs, serverNowMs } from '../engine/time.js';
import { playerSpeedMultiplier, playerTurnRadiusMultiplier } from '../render/effects.js';

function findLocalPlayer(state) {
  if (!state || !Array.isArray(state.players) || !Number.isFinite(net.localPlayerId)) return null;
  return state.players.find((p) => p.id === net.localPlayerId) || null;
}

function localPredictionCanMove(nowServer) {
  const status = authoritative.state?.status || {};
  if (!status.running || status.paused || status.gameOver) return false;
  const freezeUntilMs = Number(status.roundStartFreezeUntilMs) || 0;
  if (Number.isFinite(freezeUntilMs) && nowServer < freezeUntilMs) return false;
  const player = findLocalPlayer(authoritative.state);
  if (!player || !player.alive) return false;
  return true;
}

function clampPredictedHead(point) {
  if (!point) return point;
  const width = Math.max(1, Number(model.config.fieldWidth) || 1);
  const height = Math.max(1, Number(model.config.fieldHeight) || 1);
  const radius = Math.max(1, (Number(model.config.trailSize) || 8) / 2);
  point.x = clamp(Number(point.x) || 0, radius, width - radius);
  point.y = clamp(Number(point.y) || 0, radius, height - radius);
  if (!Number.isFinite(point.angle)) point.angle = 0;
  return point;
}

// Snapshot arrival jitter, measured as deviation from the running mean of
// (client receive time - snapshot serverNow). The absolute offset also embeds
// the clock difference and one-way delay, but only the deviation matters: it
// is exactly the arrival noise the interpolation delay has to absorb.
let arrivalOffsetEmaMs = null;
let arrivalJitterEmaMs = 0;
let displayedRenderDelayMs = null;
let lastRenderDelayAtMs = 0;
let displayedPredictionLeadMs = IDLE_PREDICTION_LEAD_MAX_MS;
let lastPredictionLeadAtMs = 0;
let lastPredictionServerNow = 0;
const PREDICTION_LEAD_RELEASE_SMOOTH_MS = 160;
const PREDICTION_LEAD_RELEASE_MAX_RATE = 0.75;

function observeSnapshotArrival(serverNow) {
  const offset = Date.now() - Number(serverNow);
  if (!Number.isFinite(offset)) return;
  if (arrivalOffsetEmaMs === null) {
    arrivalOffsetEmaMs = offset;
    return;
  }
  const deviation = Math.abs(offset - arrivalOffsetEmaMs);
  arrivalJitterEmaMs = arrivalJitterEmaMs * 0.9 + deviation * 0.1;
  arrivalOffsetEmaMs = arrivalOffsetEmaMs * 0.9 + offset * 0.1;
}

function snapshotRenderDelayMs() {
  if (USE_AUTHORITATIVE_SNAPSHOTS_ONLY) return 0;
  // One snapshot interval of delay guarantees a newer snapshot to interpolate
  // toward when arrivals are clean; the jitter term widens the buffer just
  // enough to keep that guarantee on a noisy connection. Local steering feel
  // is unaffected: the local player is drawn from prediction, not this sample.
  const interval = snapshotIntervalMs > 0
    ? snapshotIntervalMs
    : Math.max(1, Number(model.config.tickMs) || 20);
  const target = clamp(
    interval + SNAPSHOT_INTERP_BASE_MARGIN_MS + arrivalJitterEmaMs * SNAPSHOT_INTERP_JITTER_MULT,
    SNAPSHOT_RENDER_DELAY_MIN_MS,
    SNAPSHOT_RENDER_DELAY_MAX_MS
  );
  const now = renderNowMs();
  if (displayedRenderDelayMs === null) {
    displayedRenderDelayMs = target;
    lastRenderDelayAtMs = now;
    return displayedRenderDelayMs;
  }
  const budget = Math.max(0, now - lastRenderDelayAtMs) * (SNAPSHOT_RENDER_DELAY_SLEW_MS_PER_SEC / 1000);
  displayedRenderDelayMs += clamp(target - displayedRenderDelayMs, -budget, budget);
  lastRenderDelayAtMs = now;
  return displayedRenderDelayMs;
}

// The lead cap freezes the head whenever it binds: the predicted pose is
// pinned to `anchor + cap`, so once the newest anchor is older than the cap
// the head stops until a fresher one lands, then jumps. The idle floor has to
// clear the oldest the newest anchor ever gets -- one snapshot interval (two
// ticks while nobody steers) plus the one-way delay, plus arrival jitter --
// or a player holding no key watches their own snake stutter at the snapshot
// rate. Going straight is also the case prediction gets exactly right, so the
// extra lead costs no accuracy; it stays well under the steering cap.
function idlePredictionLeadMs() {
  const interval = snapshotIntervalMs > 0
    ? snapshotIntervalMs
    : Math.max(1, Number(model.config.tickMs) || 20) * 2;
  const reachMs = interval
    + estimatedOneWayMs()
    + arrivalJitterEmaMs * SNAPSHOT_INTERP_JITTER_MULT
    + IDLE_PREDICTION_LEAD_MARGIN_MS;
  return clamp(reachMs, IDLE_PREDICTION_LEAD_MAX_MS, activePredictionLeadMaxMs());
}

function predictionLeadCapMs() {
  const nowMs = renderNowMs();
  const responsive = predictionResponsivenessActive(nowMs);
  const activeLeadMaxMs = activePredictionLeadMaxMs();
  const idleLeadMs = idlePredictionLeadMs();
  if (responsive) {
    displayedPredictionLeadMs = activeLeadMaxMs;
  } else {
    const elapsedMs = lastPredictionLeadAtMs > 0
      ? clamp(nowMs - lastPredictionLeadAtMs, 0, 100)
      : 0;
    // Relaxing the lead walks the sampled instant backwards, so a release
    // faster than real time would stall or even reverse the head while it
    // settles. Keep the sampled timeline moving forwards throughout.
    const releaseRate = Math.min(
      (activeLeadMaxMs - idleLeadMs) / PREDICTION_LEAD_RELEASE_SMOOTH_MS,
      PREDICTION_LEAD_RELEASE_MAX_RATE
    );
    displayedPredictionLeadMs = Math.max(
      idleLeadMs,
      displayedPredictionLeadMs - elapsedMs * releaseRate
    );
  }
  lastPredictionLeadAtMs = nowMs;
  return displayedPredictionLeadMs;
}

function activePredictionLeadMaxMs() {
  return clamp(
    Number(model.config.predictionHorizonMs) || ACTIVE_PREDICTION_LEAD_MAX_MS,
    IDLE_PREDICTION_LEAD_MAX_MS,
    1000
  );
}

function predictionResponsivenessActive(nowMs = Date.now()) {
  const activeTurnInput = keyState.left !== keyState.right;
  const recentlyChanged = net.lastControlChangeClientMs > 0
    && (nowMs - net.lastControlChangeClientMs) <= CONTROL_RESPONSIVE_WINDOW_MS;
  return activeTurnInput || recentlyChanged || net.pendingInputs.length > 0;
}

function localHeadAttachMaxPx(player, allowPredictionLead) {
  const idleBudget = Math.max(0.75, LOCAL_HEAD_TRAIL_ATTACH_MAX_PX);
  if (!allowPredictionLead) return idleBudget;

  const bikeSpeed = Number(model.config.bikeSpeed) || 90;
  const speedState = net.predictionAnchor?.effect ? net.predictionAnchor : player;
  const speed = bikeSpeed * playerSpeedMultiplier(speedState);
  const fullPredictionDistance = speed * (activePredictionLeadMaxMs() / 1000);
  return Math.min(
    LOCAL_HEAD_ACTIVE_ATTACH_MAX_PX,
    Math.max(idleBudget, fullPredictionDistance + idleBudget)
  );
}

// --- Measured per-player motion (ported from the CurveCrash smoothing
// overlay). Each player self-calibrates to the interval at which snapshots
// actually commit its pose; extrapolation then bridges the whole gap to the
// next expected snapshot along the measured arc (midpoint rule) instead of
// advancing a fixed 12 ms straight ahead and stalling for the rest of the
// gap. EMAs smooth tick noise the same way the overlay's did.
const remoteMotionStats = new Map();
let snapshotIntervalMs = 0;
let lastSnapshotServerNow = 0;

function resetRemoteMotion() {
  remoteMotionStats.clear();
  snapshotIntervalMs = 0;
  lastSnapshotServerNow = 0;
  arrivalOffsetEmaMs = null;
  arrivalJitterEmaMs = 0;
  displayedRenderDelayMs = null;
  lastRenderDelayAtMs = 0;
  displayedPredictionLeadMs = IDLE_PREDICTION_LEAD_MAX_MS;
  lastPredictionLeadAtMs = 0;
  lastPredictionServerNow = 0;
  net.predictionCorrection = { x: 0, y: 0, angle: 0, lastAtMs: 0 };
  resetRemoteDisplaySmoothing();
}

function advancePredictionInputBase(anchorServerNow) {
  const history = Array.isArray(net.predictionInputHistory) ? net.predictionInputHistory : [];
  let baseInput = {
    left: Boolean(net.predictionBaseInput?.left),
    right: Boolean(net.predictionBaseInput?.right)
  };
  let write = 0;
  for (let i = 0; i < history.length; i += 1) {
    const edge = history[i];
    const applyServerMs = Number(edge?.applyServerMs ?? edge?.sentServerMs);
    if (
      edge?.acked === true
      && Number.isFinite(applyServerMs)
      && applyServerMs <= anchorServerNow + LOCAL_INPUT_APPLY_EPSILON_MS
    ) {
      baseInput = { left: Boolean(edge.left), right: Boolean(edge.right) };
      continue;
    }
    history[write] = edge;
    write += 1;
  }
  history.length = write;
  net.predictionBaseInput = baseInput;
  return baseInput;
}

function updateRemoteMotion(state) {
  const serverNow = Number(state?.serverNow);
  if (!Number.isFinite(serverNow)) return;
  if (lastSnapshotServerNow > 0) {
    const dt = serverNow - lastSnapshotServerNow;
    if (dt < 0) {
      // Server clock went backwards: new room/session, start fresh.
      resetRemoteMotion();
    } else if (dt > 0 && dt < MOTION_MAX_COMMIT_GAP_MS) {
      snapshotIntervalMs = snapshotIntervalMs
        ? snapshotIntervalMs * 0.7 + dt * 0.3
        : dt;
    }
  }
  lastSnapshotServerNow = serverNow;

  const seen = new Set();
  for (const p of state?.players || []) {
    const id = Number(p?.id);
    if (!Number.isFinite(id)) continue;
    seen.add(id);
    const x = Number(p.x) || 0;
    const y = Number(p.y) || 0;
    const angle = Number(p.angle) || 0;
    const spawnAtMs = Number(p.spawnAtMs);
    let s = remoteMotionStats.get(id);
    if (!s) {
      s = { x, y, angle, serverNow, spawnAtMs, vx: 0, vy: 0, omega: 0 };
      remoteMotionStats.set(id, s);
      continue;
    }
    const dt = serverNow - s.serverNow;
    if (dt <= 0) continue;
    const poseBreak = !p.alive
      || p.isFlying
      || p.isGapping
      || dt >= MOTION_MAX_COMMIT_GAP_MS
      || (Number.isFinite(spawnAtMs) && Number.isFinite(s.spawnAtMs) && spawnAtMs !== s.spawnAtMs);
    if (poseBreak) {
      // Death/respawn/teleport: never predict across it.
      s.vx = 0;
      s.vy = 0;
      s.omega = 0;
    } else if (x !== s.x || y !== s.y) {
      s.vx = (x - s.x) / dt;
      s.vy = (y - s.y) / dt;
      // The server sends authoritative headings, so the turn rate comes
      // straight from the angle delta (the overlay had to infer it from
      // velocity cross products).
      const w = clamp(
        angleDiff(angle, s.angle) / dt,
        -MOTION_MAX_OMEGA_RAD_PER_MS,
        MOTION_MAX_OMEGA_RAD_PER_MS
      );
      s.omega = s.omega ? s.omega * 0.5 + w * 0.5 : w;
    } else {
      s.vx = 0;
      s.vy = 0;
      s.omega = 0;
    }
    s.x = x;
    s.y = y;
    s.angle = angle;
    s.serverNow = serverNow;
    s.spawnAtMs = spawnAtMs;
  }
  for (const id of remoteMotionStats.keys()) {
    if (!seen.has(id)) remoteMotionStats.delete(id);
  }
}

// How far past the newest snapshot the renderer may extrapolate. With the
// interpolation delay in front, extrapolation only engages once a packet is
// later than the whole buffer; keep the head moving through such a stall for
// about one more buffer's worth (the display-offset smoothing absorbs the
// correction when real data lands), bounded for dead connections.
function snapshotExtrapCapMs() {
  const lead = snapshotIntervalMs > 0
    ? snapshotIntervalMs * SNAPSHOT_EXTRAP_LEAD
    : SNAPSHOT_EXTRAP_MAX_MS;
  const throughStall = (displayedRenderDelayMs || 0) + (snapshotIntervalMs || 0) * 3;
  return clamp(
    Math.max(lead, throughStall),
    SNAPSHOT_EXTRAP_MAX_MS,
    SNAPSHOT_EXTRAP_HARD_MAX_MS
  );
}

function pushSnapshot(snapshotState, simTick, serverNow) {
  const cloned = toSnapshotStruct(snapshotState, authoritative.state, { includeEvents: false });
  updateRemoteMotion(cloned);
  const sentServerNow = Number(serverNow) || Number(cloned.serverNow) || serverNowMs();
  const poseServerNow = Number(cloned.serverNow) || sentServerNow;
  observeSnapshotArrival(sentServerNow);
  const receivedAtMs = Date.now();
  const previous = net.snapshotBuffer[net.snapshotBuffer.length - 1];
  if (previous && Number.isFinite(Number(previous.receivedAtMs))) {
    const gapMs = Math.max(0, receivedAtMs - Number(previous.receivedAtMs));
    net.debugStats.snapshotGapMs = gapMs;
    net.debugStats.snapshotGapMaxMs = Math.max(net.debugStats.snapshotGapMaxMs, gapMs);
  }
  net.snapshotBuffer.push({
    state: cloned,
    simTick: Number(simTick) || Number(cloned.tick) || 0,
    // Interpolation follows the time represented by the pose. The envelope
    // timestamp can be newer when the server catches up several ticks at once.
    serverNow: poseServerNow,
    sentServerNow,
    receivedAtMs
  });
  const newestServerNow = Number(net.snapshotBuffer[net.snapshotBuffer.length - 1]?.serverNow);
  while (
    net.snapshotBuffer.length > 2
    && Number.isFinite(newestServerNow)
    && newestServerNow - Number(net.snapshotBuffer[1]?.serverNow) > SNAPSHOT_BUFFER_RETENTION_MS
  ) {
    net.snapshotBuffer.shift();
  }
  if (net.snapshotBuffer.length > SNAPSHOT_BUFFER_LIMIT) {
    net.snapshotBuffer.splice(0, net.snapshotBuffer.length - SNAPSHOT_BUFFER_LIMIT);
  }
  net.debugStats.snapshotBufferHighWater = Math.max(
    net.debugStats.snapshotBufferHighWater,
    net.snapshotBuffer.length
  );
}

function extrapolateSnapshotState(baseState, deltaMs) {
  const next = cloneFrameState(baseState);
  if (deltaMs <= 0) return next;
  const status = next.status || {};
  const canMove = status.running && !status.paused && !status.gameOver
    && (Number(next.serverNow) + deltaMs) >= (Number(status.roundStartFreezeUntilMs) || 0);
  if (!canMove) {
    next.serverNow = Number(next.serverNow) + deltaMs;
    return next;
  }
  const bikeSpeed = Number(model.config.bikeSpeed) || 90;
  const tickDelta = deltaMs / (Math.max(1, Number(model.config.tickMs) || 16));
  const baseServerNow = Number(baseState?.serverNow);
  for (const p of next.players || []) {
    if (!p.alive) continue;
    const s = remoteMotionStats.get(Number(p.id));
    // Measured stats only apply when they were computed from this exact
    // snapshot; an older base falls back to the config-speed straight line.
    const statsMatchBase = s
      && Number.isFinite(baseServerNow)
      && Math.abs(s.serverNow - baseServerNow) < 1;
    if (statsMatchBase && Math.hypot(s.vx, s.vy) >= MOTION_MIN_SPEED_PX_PER_MS) {
      // Midpoint rule: rotating the velocity by half the arc angle lands the
      // prediction on the arc's chord, so turning snakes follow their curve.
      // At omega = 0 this is exactly the straight-line prediction.
      const half = s.omega * deltaMs * 0.5;
      const cosH = Math.cos(half);
      const sinH = Math.sin(half);
      p.x += (s.vx * cosH - s.vy * sinH) * deltaMs;
      p.y += (s.vx * sinH + s.vy * cosH) * deltaMs;
      p.angle = Number(p.angle) + s.omega * deltaMs;
    } else {
      const speed = bikeSpeed * playerSpeedMultiplier(p);
      const dist = speed * (deltaMs / 1000);
      p.x += Math.cos(p.angle) * dist;
      p.y += Math.sin(p.angle) * dist;
    }
  }
  next.serverNow = Number(next.serverNow) + deltaMs;
  next.tick = (Number(next.tick) || 0) + tickDelta;
  return next;
}

function interpolateStates(a, b, t) {
  const srcA = a && typeof a === 'object' ? a : {};
  const srcB = b && typeof b === 'object' ? b : {};
  const playersA = Array.isArray(srcA.players) ? srcA.players : [];
  const playersB = Array.isArray(srcB.players) ? srcB.players : [];
  const next = {
    ...srcB,
    status: cloneSnapshotStatus(srcB.status),
    players: []
  };

  let canFastPath = playersA.length === playersB.length && playersB.length > 0;
  if (canFastPath) {
    for (let i = 0; i < playersB.length; i += 1) {
      const pa = playersA[i];
      const pb = playersB[i];
      if (!pa || !pb || pa.id !== pb.id) {
        canFastPath = false;
        break;
      }
    }
  }

  if (playersB.length === 0) {
    next.players = [];
  } else if (canFastPath) {
    const nextPlayers = new Array(playersB.length);
    for (let i = 0; i < playersB.length; i += 1) {
      const playerA = playersA[i];
      const playerB = playersB[i];
      if (!shouldInterpolatePlayerPose(playerA, playerB)) {
        nextPlayers[i] = {
          ...playerB
        };
        continue;
      }
      nextPlayers[i] = {
        ...playerB,
        x: lerp(Number(playerA.x) || 0, Number(playerB.x) || 0, t),
        y: lerp(Number(playerA.y) || 0, Number(playerB.y) || 0, t),
        angle: lerpAngle(Number(playerA.angle) || 0, Number(playerB.angle) || 0, t)
      };
    }
    next.players = nextPlayers;
  } else {
    const playersAById = new Map(playersA.map((p) => [p.id, p]));
    next.players = playersB.map((playerB) => {
      const playerA = playersAById.get(playerB.id);
      if (!playerA) {
        return {
          ...playerB
        };
      }
      if (!shouldInterpolatePlayerPose(playerA, playerB)) {
        return {
          ...playerB
        };
      }
      return {
        ...playerB,
        x: lerp(Number(playerA.x) || 0, Number(playerB.x) || 0, t),
        y: lerp(Number(playerA.y) || 0, Number(playerB.y) || 0, t),
        angle: lerpAngle(Number(playerA.angle) || 0, Number(playerB.angle) || 0, t)
      };
    });
  }
  next.serverNow = lerp(Number(srcA.serverNow) || 0, Number(srcB.serverNow) || 0, t);
  next.tick = lerp(Number(srcA.tick) || 0, Number(srcB.tick) || 0, t);
  return next;
}

function shouldInterpolatePlayerPose(playerA, playerB) {
  if (!playerA || !playerB) return false;
  if (Boolean(playerA.alive) !== Boolean(playerB.alive)) return false;
  if (Boolean(playerA.isFlying) !== Boolean(playerB.isFlying)) return false;
  if (Boolean(playerA.isGapping) !== Boolean(playerB.isGapping)) return false;
  const spawnA = Number(playerA.spawnAtMs);
  const spawnB = Number(playerB.spawnAtMs);
  if (Number.isFinite(spawnA) && Number.isFinite(spawnB) && spawnA !== spawnB) return false;
  return true;
}

function sampleRenderStateRaw(nowServerMs) {
  if (net.snapshotBuffer.length === 0) {
    return cloneFrameState(authoritative.state);
  }
  if (USE_AUTHORITATIVE_SNAPSHOTS_ONLY) {
    return cloneFrameState(net.snapshotBuffer[net.snapshotBuffer.length - 1].state);
  }
  const targetServerMs = nowServerMs - snapshotRenderDelayMs();
  // Once a render target has passed a snapshot, retain exactly one older pose
  // around the interpolation point. This keeps lookup work bounded without
  // dropping the pair needed to interpolate the current frame.
  while (
    net.snapshotBuffer.length > 2
    && Number(net.snapshotBuffer[1]?.serverNow) <= targetServerMs
  ) {
    net.snapshotBuffer.shift();
  }
  let older = null;
  let newer = null;
  for (const snap of net.snapshotBuffer) {
    if (snap.serverNow <= targetServerMs) {
      older = snap;
      continue;
    }
    newer = snap;
    break;
  }

  if (!older) {
    const first = net.snapshotBuffer[0];
    return cloneFrameState(first.state);
  }

  if (!newer) {
    const delta = clamp(targetServerMs - older.serverNow, 0, snapshotExtrapCapMs());
    return extrapolateSnapshotState(older.state, delta);
  }

  const dt = newer.serverNow - older.serverNow;
  const t = dt <= 0 ? 1 : clamp((targetServerMs - older.serverNow) / dt, 0, 1);
  return interpolateStates(older.state, newer.state, t);
}

// Per-remote-player display offsets: when the sampled pose jumps implausibly
// between two render frames (a late packet outran the interpolation buffer,
// or an extrapolated stretch got corrected), the jump is folded into an
// offset that decays over REMOTE_DISPLAY_SMOOTH_TAU_MS. The head keeps moving
// at its real speed and glides back onto the true path instead of teleporting.
const remoteDisplayState = new Map();

function resetRemoteDisplaySmoothing() {
  remoteDisplayState.clear();
}

function applyRemoteDisplaySmoothing(renderState) {
  const nowMs = renderNowMs();
  const players = renderState?.players || [];
  const seen = new Set();
  for (let i = 0; i < players.length; i += 1) {
    const p = players[i];
    const id = Number(p?.id);
    if (!Number.isFinite(id) || id === net.localPlayerId) continue;
    seen.add(id);
    let s = remoteDisplayState.get(id);
    if (!s) {
      s = {
        x: Number(p.x) || 0,
        y: Number(p.y) || 0,
        angle: Number(p.angle) || 0,
        offX: 0,
        offY: 0,
        offA: 0,
        spawnAtMs: Number(p.spawnAtMs),
        lastMs: nowMs
      };
      remoteDisplayState.set(id, s);
      continue;
    }
    const dtMs = Math.max(0, nowMs - s.lastMs);
    s.lastMs = nowMs;
    const spawnAtMs = Number(p.spawnAtMs);
    const poseBreak = !p.alive || p.isFlying || p.isGapping
      || (Number.isFinite(spawnAtMs) && Number.isFinite(s.spawnAtMs) && spawnAtMs !== s.spawnAtMs)
      || dtMs > MOTION_MAX_COMMIT_GAP_MS;
    if (poseBreak) {
      s.offX = 0;
      s.offY = 0;
      s.offA = 0;
      s.x = Number(p.x) || 0;
      s.y = Number(p.y) || 0;
      s.angle = Number(p.angle) || 0;
      s.spawnAtMs = spawnAtMs;
      continue;
    }

    const decay = Math.exp(-dtMs / REMOTE_DISPLAY_SMOOTH_TAU_MS);
    s.offX *= decay;
    s.offY *= decay;
    s.offA *= decay;

    const x = Number(p.x) || 0;
    const y = Number(p.y) || 0;
    const angle = Number(p.angle) || 0;
    const stepX = x - s.x;
    const stepY = y - s.y;
    const step = Math.hypot(stepX, stepY);
    const speedPxPerMs = ((Number(model.config.bikeSpeed) || 90) / 1000) * playerSpeedMultiplier(p);
    const plausibleStep = REMOTE_DISPLAY_FOLD_MIN_PX + speedPxPerMs * dtMs * REMOTE_DISPLAY_FOLD_SPEED_MULT;
    if (step > plausibleStep && step > 0) {
      // Fold only the excess beyond real motion, so the displayed head keeps
      // advancing at its true speed along the correction direction.
      const foldFraction = (step - speedPxPerMs * dtMs) / step;
      s.offX -= stepX * foldFraction;
      s.offY -= stepY * foldFraction;
    }
    const stepA = angleDiff(angle, s.angle);
    const plausibleTurn = REMOTE_DISPLAY_FOLD_MIN_RAD + MOTION_MAX_OMEGA_RAD_PER_MS * dtMs;
    if (Math.abs(stepA) > plausibleTurn) {
      s.offA -= stepA - Math.sign(stepA) * MOTION_MAX_OMEGA_RAD_PER_MS * dtMs;
    }

    if (
      Math.hypot(s.offX, s.offY) > REMOTE_DISPLAY_MAX_OFFSET_PX
      || Math.abs(s.offA) > REMOTE_DISPLAY_MAX_OFFSET_RAD
    ) {
      // Too far behind reality (teleport, resync): snap instead of gliding.
      s.offX = 0;
      s.offY = 0;
      s.offA = 0;
    }

    s.x = x;
    s.y = y;
    s.angle = angle;
    s.spawnAtMs = spawnAtMs;
    if (s.offX !== 0 || s.offY !== 0 || s.offA !== 0) {
      players[i] = {
        ...p,
        x: x + s.offX,
        y: y + s.offY,
        angle: angle + s.offA
      };
    }
  }
  for (const id of remoteDisplayState.keys()) {
    if (!seen.has(id)) remoteDisplayState.delete(id);
  }
  return renderState;
}

function sampleRenderState(nowServerMs) {
  const renderState = sampleRenderStateRaw(nowServerMs);
  if (USE_AUTHORITATIVE_SNAPSHOTS_ONLY) return renderState;
  return applyRemoteDisplaySmoothing(renderState);
}

function updatePredictionAnchor(state) {
  if (!ENABLE_LOCAL_PREDICTION) return;
  if (!Number.isFinite(net.localPlayerId)) return;
  const player = (state.players || []).find((p) => p.id === net.localPlayerId);
  if (!player) return;
  const previousAnchor = net.predictionAnchor;
  const anchorServerNow = Number(state.serverNow) || serverNowMs();
  const reconcileServerNow = Math.max(
    anchorServerNow,
    lastPredictionServerNow || anchorServerNow
  );
  // Compare predictions on one timeline. predictionDisplay is the pose from
  // the last browser frame, which is normally older than this snapshot. Using
  // it here turns ordinary between-frame movement into a correction on every
  // snapshot and progressively filters steering. Advancing the previous
  // anchor to the same timestamp as the new anchor isolates only the real
  // authoritative difference.
  const previousTarget = previousAnchor
    ? predictLocalState(reconcileServerNow)
    : null;
  const receivedAtMs = Date.now();
  const previousCorrection = decayPredictionCorrection(receivedAtMs);
  const anchorInput = advancePredictionInputBase(anchorServerNow);
  net.predictionAnchor = {
    x: Number(player.x) || 0,
    y: Number(player.y) || 0,
    angle: Number(player.angle) || 0,
    effect: cloneEffectState(player.effect),
    alive: Boolean(player.alive),
    isFlying: Boolean(player.isFlying),
    isGapping: Boolean(player.isGapping),
    spawnAtMs: Number(player.spawnAtMs),
    serverNow: anchorServerNow,
    input: anchorInput
  };
  const spawnChanged = Number.isFinite(Number(previousAnchor?.spawnAtMs))
    && Number.isFinite(Number(net.predictionAnchor.spawnAtMs))
    && Number(previousAnchor.spawnAtMs) !== Number(net.predictionAnchor.spawnAtMs);
  const poseBreak = !previousAnchor
    || !previousTarget
    || !net.predictionDisplay
    || Boolean(previousAnchor?.alive) !== Boolean(net.predictionAnchor.alive)
    || !net.predictionAnchor.alive
    || net.predictionAnchor.isFlying
    || net.predictionAnchor.isGapping
    || spawnChanged;
  if (poseBreak) {
    net.predictionDisplay = {
      x: net.predictionAnchor.x,
      y: net.predictionAnchor.y,
      angle: net.predictionAnchor.angle
    };
    net.predictionCorrection = { x: 0, y: 0, angle: 0, lastAtMs: 0 };
  } else {
    const target = predictLocalState(reconcileServerNow);
    const correctionX = Number(previousTarget.x) + previousCorrection.x - Number(target?.x);
    const correctionY = Number(previousTarget.y) + previousCorrection.y - Number(target?.y);
    const correctionAngle = angleDiff(
      Number(previousTarget.angle) + previousCorrection.angle,
      Number(target?.angle)
    );
    const correctionPx = Math.hypot(correctionX, correctionY);
    const correctionRad = Math.abs(correctionAngle);
    recordCorrectionMetrics(correctionPx, correctionRad);
    if (
      !target
      || !Number.isFinite(correctionPx)
      || !Number.isFinite(correctionRad)
      || correctionPx > LOCAL_CORRECTION_MAX_PX
      || correctionRad > LOCAL_CORRECTION_MAX_RAD
    ) {
      const targetX = Number(target?.x);
      const targetY = Number(target?.y);
      const targetAngle = Number(target?.angle);
      net.predictionDisplay = {
        x: Number.isFinite(targetX) ? targetX : net.predictionAnchor.x,
        y: Number.isFinite(targetY) ? targetY : net.predictionAnchor.y,
        angle: Number.isFinite(targetAngle) ? targetAngle : net.predictionAnchor.angle
      };
      net.predictionCorrection = { x: 0, y: 0, angle: 0, lastAtMs: 0 };
      recordHardCorrection();
    } else {
      // Carry the old predicted path across the anchor replacement, then decay
      // only the actual authoritative difference underneath future frames.
      net.predictionCorrection = {
        x: correctionX,
        y: correctionY,
        angle: correctionAngle,
        lastAtMs: receivedAtMs
      };
    }
  }
}

function recordCorrectionMetrics(distance, angle) {
  const dist = Number.isFinite(distance) ? Math.max(0, distance) : 0;
  const rad = Number.isFinite(angle) ? Math.max(0, angle) : 0;
  net.correctionPx = dist;
  net.correctionAngle = rad;
  net.debugStats.correctionPx = dist;
  net.debugStats.correctionAngle = rad;
  net.debugStats.correctionPxMax = Math.max(net.debugStats.correctionPxMax, dist);
  net.debugStats.correctionAngleMax = Math.max(net.debugStats.correctionAngleMax, rad);
}

function recordHardCorrection() {
  net.correctionHard = true;
  net.debugStats.hardCorrections += 1;
}

function decayPredictionCorrection(nowMs = renderNowMs()) {
  const correction = net.predictionCorrection || {};
  const lastAtMs = Number(correction.lastAtMs) || nowMs;
  const decay = Math.exp(-Math.max(0, nowMs - lastAtMs) / LOCAL_CORRECTION_SMOOTH_MS);
  const next = {
    x: (Number(correction.x) || 0) * decay,
    y: (Number(correction.y) || 0) * decay,
    angle: (Number(correction.angle) || 0) * decay,
    lastAtMs: nowMs
  };
  net.predictionCorrection = next;
  return next;
}

function applyPredictionCorrection(target) {
  const correction = decayPredictionCorrection();
  recordCorrectionMetrics(
    Math.hypot(correction.x, correction.y),
    Math.abs(correction.angle)
  );
  return {
    ...target,
    x: Number(target.x) + correction.x,
    y: Number(target.y) + correction.y,
    angle: Number(target.angle) + correction.angle
  };
}

function predictLocalState(nowServer) {
  if (!ENABLE_LOCAL_PREDICTION) return null;
  if (!net.predictionAnchor) return null;
  const anchorServerNow = Number(net.predictionAnchor.serverNow) || nowServer;
  const targetServerNow = Math.min(nowServer, anchorServerNow + predictionLeadCapMs());
  const bridgeMs = Math.max(0, targetServerNow - anchorServerNow);
  net.debugStats.predictionBridgeMs = bridgeMs;
  net.debugStats.predictionBridgeMaxMs = Math.max(net.debugStats.predictionBridgeMaxMs, bridgeMs);
  const predicted = {
    x: net.predictionAnchor.x,
    y: net.predictionAnchor.y,
    angle: net.predictionAnchor.angle,
    effect: net.predictionAnchor.effect,
    alive: net.predictionAnchor.alive
  };
  if (!predicted.alive) return predicted;
  if (!localPredictionCanMove(targetServerNow)) return predicted;

  const bikeSpeed = Number(model.config.bikeSpeed) || 90;
  const baseTurnRadius = Number(model.config.turnRadius) || 35;
  const speedMul = playerSpeedMultiplier(predicted);
  const turnRadiusMul = playerTurnRadiusMultiplier(predicted);
  const turnRadius = baseTurnRadius * turnRadiusMul;
  const speed = bikeSpeed * speedMul;
  const predictionStepMs = Math.max(1, Number(model.config.tickMs) || 20);

  let localTime = anchorServerNow;
  let currentInput = {
    left: Boolean(net.predictionAnchor.input?.left ?? net.predictionBaseInput?.left),
    right: Boolean(net.predictionAnchor.input?.right ?? net.predictionBaseInput?.right)
  };
  const resolveInputApplyServerMs = (edge, baselineServerMs) => {
    const rawApplyServerMs = Number(edge?.applyServerMs ?? edge?.sentServerMs);
    if (!Number.isFinite(rawApplyServerMs)) return baselineServerMs;
    const latestAllowed = Math.max(
      baselineServerMs,
      targetServerNow - LOCAL_INPUT_APPLY_EPSILON_MS
    );
    return clamp(rawApplyServerMs, baselineServerMs, latestAllowed);
  };
  const integrate = (dtMs, input) => {
    if (dtMs <= 0) return;
    advancePredictedPose(predicted, {
      dtMs,
      left: input.left,
      right: input.right,
      speed,
      turnRadius,
      maxStepMs: predictionStepMs
    });
  };

  for (const edge of net.predictionInputHistory) {
    const applyServerMs = resolveInputApplyServerMs(edge, localTime);
    if (applyServerMs <= localTime) {
      currentInput = { left: edge.left, right: edge.right };
      continue;
    }
    integrate(applyServerMs - localTime, currentInput);
    localTime = applyServerMs;
    currentInput = { left: edge.left, right: edge.right };
  }

  integrate(Math.max(0, targetServerNow - localTime), currentInput);
  return clampPredictedHead(predicted);
}

function updatePrediction(nowServer) {
  if (!ENABLE_LOCAL_PREDICTION) return null;
  lastPredictionServerNow = Number(nowServer) || lastPredictionServerNow;
  const rawTarget = predictLocalState(nowServer);
  if (!rawTarget) return null;
  const target = applyPredictionCorrection(rawTarget);
  if (!net.predictionDisplay) {
    net.predictionDisplay = { x: target.x, y: target.y, angle: target.angle };
    clampPredictedHead(net.predictionDisplay);
    return net.predictionDisplay;
  }
  if (!localPredictionCanMove(nowServer)) {
    net.predictionDisplay.x = target.x;
    net.predictionDisplay.y = target.y;
    net.predictionDisplay.angle = target.angle;
    clampPredictedHead(net.predictionDisplay);
    return net.predictionDisplay;
  }
  net.predictionDisplay.x = target.x;
  net.predictionDisplay.y = target.y;
  net.predictionDisplay.angle = target.angle;
  clampPredictedHead(net.predictionDisplay);
  net.correctionHard = false;
  return net.predictionDisplay;
}

function applyLocalPrediction(renderState, nowServer) {
  if (!ENABLE_LOCAL_PREDICTION) return renderState;
  if (!Number.isFinite(net.localPlayerId)) return renderState;
  const idx = (renderState.players || []).findIndex((p) => p.id === net.localPlayerId);
  if (idx === -1) return renderState;
  const sampledLocal = renderState.players[idx];
  if (sampledLocal?.alive && (sampledLocal?.isFlying || sampledLocal?.isGapping)) {
    renderState.players[idx] = {
      ...sampledLocal,
      localPrediction: false
    };
    return renderState;
  }
  const predicted = updatePrediction(nowServer);
  const authoritativeLocal = findLocalPlayer(authoritative.state);
  const nextLocal = {
    ...sampledLocal
  };

  const predictedX = Number(predicted?.x);
  const predictedY = Number(predicted?.y);
  const hasPredictedPosition = Number.isFinite(predictedX) && Number.isFinite(predictedY);
  if (hasPredictedPosition) {
    // updatePrediction() already reconciles its persistent display pose. A
    // second auth->prediction blend here made the result refresh-rate
    // dependent and discarded most of the lead on high-Hz displays.
    nextLocal.x = predictedX;
    nextLocal.y = predictedY;
    nextLocal.localPrediction = true;
  } else if (authoritativeLocal) {
    nextLocal.localPrediction = false;
    const authX = Number(authoritativeLocal.x);
    const authY = Number(authoritativeLocal.y);
    if (Number.isFinite(authX)) {
      nextLocal.x = authX;
    }
    if (Number.isFinite(authY)) {
      nextLocal.y = authY;
    }
  } else {
    nextLocal.localPrediction = false;
  }

  if (predicted && Number.isFinite(Number(predicted.angle))) {
    nextLocal.angle = Number(predicted.angle);
  } else if (authoritativeLocal && Number.isFinite(Number(authoritativeLocal.angle))) {
    nextLocal.angle = Number(authoritativeLocal.angle);
  }

  if (authoritativeLocal) {
    const authX = Number(authoritativeLocal.x);
    const authY = Number(authoritativeLocal.y);
    const headX = Number(nextLocal.x);
    const headY = Number(nextLocal.y);
    if (
      Number.isFinite(authX)
      && Number.isFinite(authY)
      && Number.isFinite(headX)
      && Number.isFinite(headY)
    ) {
      const dx = headX - authX;
      const dy = headY - authY;
      const dist = Math.hypot(dx, dy);
      const maxAttach = localHeadAttachMaxPx(nextLocal, hasPredictedPosition);
      if (dist > maxAttach) {
        const scale = maxAttach / dist;
        nextLocal.x = authX + dx * scale;
        nextLocal.y = authY + dy * scale;
      }
    }
  }

  renderState.players[idx] = nextLocal;
  return renderState;
}

export {
  pushSnapshot,
  resetRemoteMotion,
  sampleRenderState,
  updatePredictionAnchor,
  applyLocalPrediction
};
